import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, Select, MenuItem, Paper, TextField,
  Grid, InputLabel, FormControl, CircularProgress, Alert
} from "@mui/material";
import api from "../../api";
import { useNavigate } from "react-router-dom";


/**
 * BoxeSettings
 *
 * Usage:
 * - if you pass classesList prop, it will be used (normalized). Otherwise it fetches GET /classes.
 * - if you pass getElevesFromClasse prop, it will be used to fetch students; otherwise it calls GET /classes/:id/eleves.
 * - Keeps the UI you provided: manual names when no class selected; selection from class when chosen.
 *
 * When user clicks "Valider" it navigates to /jugeboxe/match passing the settings (settings contain chosen classeId and potentially nomRed/nomBlue).
 */

export default function BoxeSettings({
  nbBoxeurs = 2,
  classesList = [],
  getElevesFromClasse // optional async function(id) => array of eleves
}) {
  const [type, setType] = useState("poings");
  const [durationMin, setDurationMin] = useState(1);
  const [durationSec, setDurationSec] = useState(30);
  const [valeurPoing, setValeurPoing] = useState(1);
  const [valeurPied, setValeurPied] = useState(2);
  const [nbReprises, setNbReprises] = useState(1);

  const [classeId, setClasseId] = useState("");
  const [eleves, setEleves] = useState([]);
  const [eleveRed, setEleveRed] = useState("");
  const [eleveBlue, setEleveBlue] = useState("");
  const [nomRed, setNomRed] = useState("");
  const [nomBlue, setNomBlue] = useState("");

  const [availableClasses, setAvailableClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [classesError, setClassesError] = useState("");

  const navigate = useNavigate();

  // Normalizers
  function normalizeClasses(raw = []) {
    return raw.map(c => {
      const id = String(c._id ?? c.id ?? c.classeId ?? c.uuid ?? "");
      const nom = c.nom ?? c.name ?? c.label ?? c.title ?? `Classe ${id}`;
      return { id, nom, raw: c };
    }).filter(x => x.id);
  }

  function normalizeEleves(raw = []) {
    return raw.map(e => {
      const id = String(e._id ?? e.id ?? e.id_eleve ?? e.uuid ?? "");
      const nom = `${e.prenom ? e.prenom + " " : ""}${e.nom ?? e.name ?? e.fullname ?? id}`;
      return { id, nom, raw: e };
    }).filter(x => x.id);
  }

  // load classes: prefer prop classesList else fetch
  async function loadClasses(forceFetch = false) {
    if (!forceFetch && Array.isArray(classesList) && classesList.length > 0) {
      const normalized = normalizeClasses(classesList);
      setAvailableClasses(normalized);
      if (!classeId && normalized.length > 0) setClasseId(normalized[0].id);
      return;
    }
    setLoadingClasses(true);
    setClassesError("");
    try {
      const res = await api.get("/classes");
      const raw = Array.isArray(res.data) ? res.data : [];
      const normalized = normalizeClasses(raw);
      setAvailableClasses(normalized);
      if (!classeId && normalized.length > 0) setClasseId(normalized[0].id);
    } catch (err) {
      console.error("BoxeSettings: GET /classes failed", err);
      setClassesError(err?.response?.data?.error || err?.message || "Impossible de charger les classes.");
      setAvailableClasses([]);
    } finally {
      setLoadingClasses(false);
    }
  }

  // fetch eleves for classe
  async function fetchElevesForClasse(id) {
    if (!id) {
      setEleves([]);
      setEleveRed("");
      setEleveBlue("");
      return;
    }
    try {
      let list = [];
      if (getElevesFromClasse && typeof getElevesFromClasse === "function") {
        list = await getElevesFromClasse(id) || [];
      } else {
        const res = await api.get(`/classes/${encodeURIComponent(id)}/eleves`);
        list = Array.isArray(res.data) ? res.data : [];
      }
      const normalized = normalizeEleves(list);
      setEleves(normalized);
      // set defaults
      if (normalized.length > 0) {
        if (!eleveRed) setEleveRed(normalized[0].id);
        if (!eleveBlue) setEleveBlue(normalized.length > 1 ? normalized[1].id : normalized[0].id);
      } else {
        setEleveRed("");
        setEleveBlue("");
      }
    } catch (err) {
      console.error("BoxeSettings: failed loading eleves for classe", id, err);
      setEleves([]);
      setEleveRed("");
      setEleveBlue("");
    }
  }

  useEffect(() => {
    loadClasses(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (classeId) fetchElevesForClasse(classeId);
    else {
      setEleves([]);
      setEleveRed("");
      setEleveBlue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classeId]);

  const handleValider = () => {
    const settings = {
      type,
      durationMin,
      durationSec,
      valeurPoing,
      valeurPied,
      nbReprises,
      classeId: classeId || null,
      eleveRed: eleveRed || null,
      eleveBlue: eleveBlue || null,
      nomRed: (!classeId ? nomRed || null : null),
      nomBlue: (!classeId ? nomBlue || null : null)
    };
    // Navigate to BoxeMatch, pass settings in state
    navigate("/jugeboxe/match", { state: { settings } });
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#111", display: "flex", justifyContent: "center", alignItems: "center" }}>
      <Paper sx={{ p: 4, minWidth: 340 }}>
        <Typography variant="h5" align="center" gutterBottom>
          Paramètres du match {nbBoxeurs === 1 ? "1 boxeur" : "2 boxeurs"}
        </Typography>

        {/* Nombre de reprises */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">Nombre de reprises</Typography>
          <TextField
            type="number"
            value={nbReprises}
            onChange={e => setNbReprises(Math.max(1, Number(e.target.value)))}
            inputProps={{ min: 1, style: { width: 48, textAlign: "center" } }}
            sx={{ bgcolor: "#fff", borderRadius: 2, mt: 1 }}
          />
        </Box>

        {/* Durée */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1">Durée d'une reprise</Typography>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <TextField
                type="number"
                value={durationMin}
                onChange={e => setDurationMin(Math.max(0, Number(e.target.value)))}
                inputProps={{ min: 0, style: { width: 40, textAlign: "center" } }}
                sx={{ bgcolor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item>
              <Typography>minutes</Typography>
            </Grid>
            <Grid item>
              <TextField
                type="number"
                value={durationSec}
                onChange={e => setDurationSec(Math.max(0, Math.min(59, Number(e.target.value))))}
                inputProps={{ min: 0, max: 59, style: { width: 40, textAlign: "center" } }}
                sx={{ bgcolor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item>
              <Typography>secondes</Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Type de match */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1">Type de match</Typography>
          <Select value={type} onChange={e => setType(e.target.value)} fullWidth sx={{ bgcolor: "#fff", borderRadius: 2 }}>
            <MenuItem value="poings">Poings</MenuItem>
            <MenuItem value="piedspoings">Pieds/Poings</MenuItem>
          </Select>
        </Box>

        {/* Valeur des coups */}
        {type === "piedspoings" && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1">Valeur des coups</Typography>
            <Grid container spacing={2}>
              <Grid item>
                <TextField
                  label="Poing"
                  type="number"
                  value={valeurPoing}
                  onChange={e => setValeurPoing(Math.max(0, Number(e.target.value)))}
                  inputProps={{ min: 0, style: { width: 48, textAlign: "center" } }}
                  sx={{ bgcolor: "#fff", borderRadius: 2 }}
                />
              </Grid>
              <Grid item>
                <TextField
                  label="Pied"
                  type="number"
                  value={valeurPied}
                  onChange={e => setValeurPied(Math.max(0, Number(e.target.value)))}
                  inputProps={{ min: 0, style: { width: 48, textAlign: "center" } }}
                  sx={{ bgcolor: "#fff", borderRadius: 2 }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Sélection de la classe et des élèves */}
        {nbBoxeurs === 2 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1">Sélection de la classe (optionnel)</Typography>

            {loadingClasses ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 1 }}>
                <CircularProgress size={20} />
                <Typography>Chargement des classes...</Typography>
              </Box>
            ) : classesError ? (
              <Box sx={{ mb: 2 }}>
                <Alert severity="error" sx={{ mb: 1 }}>{classesError}</Alert>
                <Button onClick={() => loadClasses(true)} variant="outlined" size="small">Réessayer</Button>
              </Box>
            ) : (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Classe</InputLabel>
                  <Select
                    value={classeId}
                    label="Classe"
                    onChange={e => setClasseId(e.target.value)}
                    sx={{ bgcolor: "#fff", borderRadius: 2 }}
                  >
                    <MenuItem value="">Aucune</MenuItem>
                    {availableClasses.map(classe => (
                      <MenuItem value={classe.id} key={classe.id}>{classe.nom}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {classeId && (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Élève Rouge</InputLabel>
                        <Select
                          value={eleveRed}
                          label="Élève Rouge"
                          onChange={e => setEleveRed(e.target.value)}
                          sx={{ bgcolor: "#fff", borderRadius: 2 }}
                        >
                          <MenuItem value="">Aucun</MenuItem>
                          {eleves.map(el => (
                            <MenuItem value={el.id} key={el.id}>{el.nom}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Élève Bleu</InputLabel>
                        <Select
                          value={eleveBlue}
                          label="Élève Bleu"
                          onChange={e => setEleveBlue(e.target.value)}
                          sx={{ bgcolor: "#fff", borderRadius: 2 }}
                        >
                          <MenuItem value="">Aucun</MenuItem>
                          {eleves.map(el => (
                            <MenuItem value={el.id} key={el.id}>{el.nom}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                )}

                {!classeId && (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <TextField
                        label="Nom Rouge"
                        value={nomRed}
                        onChange={e => setNomRed(e.target.value)}
                        sx={{ bgcolor: "#fff", borderRadius: 2 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Nom Bleu"
                        value={nomBlue}
                        onChange={e => setNomBlue(e.target.value)}
                        sx={{ bgcolor: "#fff", borderRadius: 2 }}
                      />
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </Box>
        )}

        <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
          <Button variant="outlined" color="secondary" onClick={() => navigate(-1)}>Retour</Button>
          <Button variant="contained" color="primary" onClick={handleValider}>Lancer le match</Button>
        </Box>

        <Box sx={{ mt: 4, bgcolor: "#f8f8f8", borderRadius: 2, p: 2, border: "1px solid #ddd" }}>
          <Typography variant="subtitle1" sx={{ color: "#a51c30", fontWeight: "bold" }}>SANCTIONS</Typography>
          <Typography variant="body2" sx={{ color: "#555" }}>
            +1 : <b>MISE EN GARDE</b> &nbsp; | &nbsp;
            +2 : <b>AVERTISSEMENT</b> &nbsp; | &nbsp;
            +3 : <b style={{ color: "red" }}>DISQUALIFICATION</b> (le match est terminé)
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}